import { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useUserStore } from './user'
import Notification from '~/services/notificationService'
import type {
  CapacitySetIdentifiers,
  DirectionScope,
} from '~/models/capacitySets'
import type { GETCapacitySet, PortComplex } from '~/services/apiClient'
import {
  CapacityBucket,
  CapacitySet,
  CategorizedCapacitySets,
  DIRECTION_SCOPES,
  directionScopeFromAPI,
  directionScopeKey,
  IMPORT_LOAD,
  makeCapacitySetFromAPI,
} from '~/models/capacitySets'
import { AppointmentCapacitiesApi } from '~/services/apiClient'

/** Composite key so Import and Empty sets at the same port never collide in the same map. */
function portScopeKey(port: PortComplex, scope: DirectionScope): string {
  return `${port}::${directionScopeKey(scope)}`
}

export const useCapacitiesStore = defineStore('capacities', () => {
  // Other stores

  const userStore = useUserStore()

  // Store everything categorized by PortComplex + direction scope
  const categorizedCapacitySets = ref(
    new Map<string, CategorizedCapacitySets>()
  )
  const activeCapacitySetKey = ref(null as null | string)
  // Which direction scope the Capacity Management screen is showing. Kept in
  // the store because websocket updates re-pick a default set (see
  // setCapacitySetsFromAPI) and would otherwise drop an Import Load set into
  // the editor while the user is looking at Empty Return.
  const viewedDirectionScope = ref<DirectionScope>(IMPORT_LOAD)
  const saving = ref(false)
  const loading = ref(false)
  const loadedFromAPI = ref(false)

  function getOrCreateCapacityCategoriesForPort(
    port: PortComplex,
    scope: DirectionScope
  ): CategorizedCapacitySets {
    const key = portScopeKey(port, scope)
    let categorizedCapacitySetsInner = categorizedCapacitySets.value.get(key)
    if (!categorizedCapacitySetsInner) {
      categorizedCapacitySetsInner = new CategorizedCapacitySets(
        new Map(),
        new Map(),
        new Map()
      )
      categorizedCapacitySets.value.set(key, categorizedCapacitySetsInner)
    }
    return categorizedCapacitySetsInner
  }

  function numCapacitySetsForAllPorts(): number {
    let total = 0
    for (const cats of categorizedCapacitySets.value.values()) {
      total += cats.fleetSets.size + cats.customerSets.size + cats.tagSets.size
    }
    return total
  }

  function firstCapacitySetForPort(
    portComplex: PortComplex,
    scope: DirectionScope
  ): CapacitySet | undefined {
    const capacitySets = categorizedCapacitySets.value.get(
      portScopeKey(portComplex, scope)
    )
    if (capacitySets) {
      return (
        capacitySets.fleetSets.values().next().value ??
        capacitySets.customerSets.values().next().value ??
        capacitySets.tagSets.values().next().value
      )
    }
    return undefined
  }

  const activeCapacitySet = computed((): CapacitySet | null => {
    if (activeCapacitySetKey.value) {
      // set.key may itself contain hyphens, so join the remainder back together.
      const [portComplex, scopeKey, ...keyParts] =
        activeCapacitySetKey.value.split('::')
      const key = keyParts.join('::')
      const capacitySets = categorizedCapacitySets.value.get(
        `${portComplex}::${scopeKey}`
      )
      return capacitySets?.allSets.get(key) ?? null
    }
    return null
  })

  // Functions
  function loadBucketsIfNotLoaded() {
    // No Need to load if we've loaded in this same session. Websockets will keep
    // us up to date. Also, these are unlikely to change often
    if (loadedFromAPI.value) return Promise.resolve()
    loading.value = true
    const api = new AppointmentCapacitiesApi()
    return Promise.all(
      DIRECTION_SCOPES.map((scope) =>
        api.getAppointmentCapacitySetsAppointmentsCapacitiesSetsGet(
          undefined,
          scope.direction,
          scope.loaded,
          undefined
        )
      )
    )
      .then((responses) => {
        for (const resp of responses) {
          setCapacitySetsFromAPI(resp.data.capacity_sets)
        }
        loadedFromAPI.value = true
      })
      .finally(() => {
        loading.value = false
      })
  }
  function setCapacitySetsFromAPI(capacitySets: GETCapacitySet[]) {
    for (const apiCapacitySet of capacitySets) {
      if (apiCapacitySet.buckets.length === 0) {
        // deletion websocket update — remove the specific set by id
        removeCapacitySetFromStoreById(
          apiCapacitySet.id,
          apiCapacitySet.port_complex,
          directionScopeFromAPI(apiCapacitySet)
        )
        continue
      }
      const set = makeCapacitySetFromAPI(apiCapacitySet, userStore.demo_mode)
      setCapacitySet(set)
    }
    if (numCapacitySetsForAllPorts() > 0) {
      selectDefaultCapacitySet(
        userStore.availablePortComplexes[0],
        viewedDirectionScope.value
      )
    }
  }
  function setCapacitySet(set: CapacitySet) {
    const capacitySets = getOrCreateCapacityCategoriesForPort(
      set.portComplex,
      set.scope
    )
    capacitySets.allSets.set(set.key, set)
    if (set.customerID) capacitySets.customerSets.set(set.key, set)
    else if (set.containerTag) capacitySets.tagSets.set(set.key, set)
    else capacitySets.fleetSets.set(set.key, set)
  }
  function selectDefaultCapacitySet(
    portComplex: PortComplex,
    directionScope: DirectionScope
  ) {
    if (!activeCapacitySet.value) {
      const set = firstCapacitySetForPort(portComplex, directionScope)
      if (set) {
        setActiveCapacitySet(set)
      } else {
        clearActiveCapacitySet()
      }
    }
  }
  function setActiveCapacitySet(capacitySet: CapacitySet) {
    // Validate exists in nested allSets
    const capacitySets = categorizedCapacitySets.value.get(
      portScopeKey(capacitySet.portComplex, capacitySet.scope)
    )
    const exists = !!capacitySets?.allSets.get(capacitySet.key)
    if (!exists) throw new Error(`Capacity set ${capacitySet.key} not found`)
    activeCapacitySetKey.value = `${capacitySet.portComplex}::${directionScopeKey(capacitySet.scope)}::${capacitySet.key}`
  }
  function getCapacitySetById(
    id: number,
    portComplex: PortComplex,
    directionScope: DirectionScope
  ): CapacitySet | undefined {
    const portCapacitySets = categorizedCapacitySets.value.get(
      portScopeKey(portComplex, directionScope)
    )
    if (!portCapacitySets) return undefined
    for (const set of portCapacitySets.allSets.values()) {
      if (set.id === id) return set
    }
    return undefined
  }
  function clearActiveCapacitySet() {
    activeCapacitySetKey.value = null
  }
  async function saveCapacitySet(
    capacitySet: CapacitySet
  ): Promise<CapacitySet> {
    const api = new AppointmentCapacitiesApi()
    saving.value = true
    const APIFormattedBuckets = capacitySet.buckets.map((b) => {
      return {
        day_of_week: b.dayOfWeek,
        hour_of_day: b.hourOfDay,
        max_appointments: b.maxAppointments,
      }
    })
    const body = {
      timezone: DateTime.now().toFormat('z'),
      buckets: APIFormattedBuckets,
      customer_id: capacitySet.customerID || undefined,
      container_tag: capacitySet.containerTag || undefined,
      independent_capacity: capacitySet.dedicated,
      port_complex: capacitySet.portComplex,
      direction: capacitySet.scope.direction,
      loaded: capacitySet.scope.loaded,
    }
    try {
      const response =
        capacitySet.id !== null
          ? await api.updateCapacitySetAppointmentsCapacitiesSetsSetIdPut(
              capacitySet.id,
              body
            )
          : await api.createCapacitySetAppointmentsCapacitiesSetsPost(body)
      // Drop the pre-save entry — its key may differ if bucket width changed
      removeCapacitySetFromStore(capacitySet)
      const savedSet = makeCapacitySetFromAPI(
        response.data,
        userStore.demo_mode
      )
      setCapacitySet(savedSet)
      setActiveCapacitySet(savedSet)
      Notification.success({ title: 'Capacities saved' })
      return savedSet
    } finally {
      saving.value = false
    }
  }
  async function deleteCapacitySet(set: CapacitySet) {
    if (set.id === null) {
      // Unsaved — just discard locally
      removeCapacitySetFromStore(set)
      activeCapacitySetKey.value = null
      selectDefaultCapacitySet(set.portComplex, set.scope)
      return
    }
    saving.value = true
    const api = new AppointmentCapacitiesApi()
    try {
      await api.deleteCapacitySetByIdAppointmentsCapacitiesSetsSetIdDelete(
        set.id
      )
      removeCapacitySetFromStore(set)
      activeCapacitySetKey.value = null
      Notification.success({ title: 'Capacities Updated' })
    } finally {
      selectDefaultCapacitySet(set.portComplex, set.scope)
      saving.value = false
    }
  }
  async function setIndependentCapacityForScope(
    scope: {
      customer_id?: number
      container_tag?: string
      port_complex: PortComplex
    },
    directionScope: DirectionScope,
    independentCapacity: boolean
  ) {
    const api = new AppointmentCapacitiesApi()
    saving.value = true
    try {
      await api.patchScopeIndependentCapacityAppointmentsCapacitiesSetsScopeIndependentCapacityPatch(
        {
          customer_id: scope.customer_id,
          container_tag: scope.container_tag,
          port_complex: scope.port_complex,
          direction: directionScope.direction,
          loaded: directionScope.loaded,
          independent_capacity: independentCapacity,
        }
      )
      // Reflect on every sibling set in the scope (same port AND direction scope)
      const portCapacitySets = categorizedCapacitySets.value.get(
        portScopeKey(scope.port_complex, directionScope)
      )
      if (portCapacitySets) {
        const targetMap = scope.customer_id
          ? portCapacitySets.customerSets
          : scope.container_tag
            ? portCapacitySets.tagSets
            : portCapacitySets.fleetSets
        const matching = Array.from(targetMap.values()).filter((s) => {
          if (scope.customer_id) return s.customerID === scope.customer_id
          if (scope.container_tag) return s.containerTag === scope.container_tag
          return true
        })
        for (const oldSet of matching) {
          const newBuckets = oldSet.buckets.map(
            (b) =>
              new CapacityBucket(
                {
                  startMinuteOfWeek: b.startMinuteOfWeek,
                  bucketMinutes: b.bucketMinutes,
                  maxAppointments: b.maxAppointments,
                  customer: b.customer,
                  containerTag: b.containerTag,
                  dedicated: independentCapacity,
                  portComplex: b.portComplex,
                },
                false
              )
          )
          const newSet = new CapacitySet(
            oldSet.id,
            newBuckets,
            oldSet.bucketMinutes,
            oldSet.customer,
            oldSet.containerTag,
            independentCapacity,
            oldSet.portComplex,
            oldSet.scope,
            oldSet.lastEditedBy,
            oldSet.lastEdited,
            oldSet.saved
          )
          const wasActive =
            activeCapacitySetKey.value ===
            `${oldSet.portComplex}::${directionScopeKey(oldSet.scope)}::${oldSet.key}`
          removeCapacitySetFromStore(oldSet)
          setCapacitySet(newSet)
          if (wasActive) setActiveCapacitySet(newSet)
        }
      }
      Notification.success({ title: 'Capacities Updated' })
    } finally {
      saving.value = false
    }
  }

  function removeCapacitySetFromStore(set: CapacitySet) {
    const capacitySets = getOrCreateCapacityCategoriesForPort(
      set.portComplex,
      set.scope
    )
    if (set.customerID) capacitySets.customerSets.delete(set.key)
    else if (set.containerTag) capacitySets.tagSets.delete(set.key)
    else capacitySets.fleetSets.delete(set.key)
    capacitySets.allSets.delete(set.key)
  }
  function removeCapacitySetFromStoreById(
    id: number,
    portComplex: PortComplex,
    directionScope: DirectionScope
  ) {
    const set = getCapacitySetById(id, portComplex, directionScope)
    if (set) removeCapacitySetFromStore(set)
  }

  function activateCapacityManagement() {
    generateBuckets(
      120,
      {
        customer: null,
        containerTag: null,
        portComplex: userStore.availablePortComplexes[0],
      },
      IMPORT_LOAD,
      false
    )
  }
  function generateBuckets(
    bucketMinutes: number,
    setIdentifiers: CapacitySetIdentifiers,
    directionScope: DirectionScope,
    dedicated: boolean
  ) {
    const buckets = generateEmpty(
      bucketMinutes,
      setIdentifiers,
      dedicated,
      userStore.demo_mode
    )
    const capacitySet = new CapacitySet(
      null,
      buckets,
      bucketMinutes,
      setIdentifiers.customer,
      setIdentifiers.containerTag,
      dedicated,
      setIdentifiers.portComplex,
      directionScope,
      null,
      DateTime.now(),
      false // Not saved
    )
    setCapacitySet(capacitySet)
    setActiveCapacitySet(capacitySet)
  }
  // Computed properties
  function getCapacitySets(
    customerID: number | null,
    containerTags: string[],
    portComplex: PortComplex,
    directionScope: DirectionScope
  ) {
    const capacitySets: CapacitySet[] = []
    const portCapacitySets = categorizedCapacitySets.value.get(
      portScopeKey(portComplex, directionScope)
    )
    if (!portCapacitySets) return capacitySets

    // Customer sets — every sibling at this scope (different bucket widths)
    let customerHasDedicatedSet = false
    if (customerID) {
      for (const set of portCapacitySets.customerSets.values()) {
        if (set.customerID === customerID) {
          capacitySets.push(set)
          if (set.dedicated) customerHasDedicatedSet = true
        }
      }
    }
    // Fleet sets apply unless this customer has a dedicated set
    if (!customerHasDedicatedSet) {
      for (const fleetSet of portCapacitySets.fleetSets.values()) {
        capacitySets.push(fleetSet)
      }
    }
    // Tag sets — every sibling at any matching tag scope
    for (const containerTag of containerTags) {
      for (const set of portCapacitySets.tagSets.values()) {
        if (set.containerTag === containerTag) {
          capacitySets.push(set)
        }
      }
    }
    return capacitySets
  }
  function getBucketsForTime(
    time: DateTime,
    customerID: number | null,
    containerTags: string[],
    portComplex: PortComplex,
    directionScope: DirectionScope
  ): CapacityBucket[] {
    const capacitySets = getCapacitySets(
      customerID,
      containerTags,
      portComplex,
      directionScope
    )
    return capacitySets.map((capacitySet) => capacitySet.getBucket(time))
  }

  const setup = computed((): boolean => {
    return numCapacitySetsForAllPorts() > 0 && !loading.value
  })

  function getDedicatedCustomerCapacitySetIdsForPort(
    portComplex: PortComplex,
    directionScope: DirectionScope
  ) {
    const ids = new Set<number>()
    const portCapacitySets = categorizedCapacitySets.value.get(
      portScopeKey(portComplex, directionScope)
    )
    if (portCapacitySets) {
      for (const capacitySet of portCapacitySets.customerSets.values()) {
        if (capacitySet.dedicated) {
          if (capacitySet.customerID === null) {
            throw new Error(
              'Dedicated customer capacity set must have a customer ID'
            )
          }
          ids.add(capacitySet.customerID)
        }
      }
    }
    return Array.from(ids)
  }

  function getDedicatedTagCapacityIdsForPort(
    portComplex: PortComplex,
    directionScope: DirectionScope
  ) {
    const tags = new Set<string>()
    const portCapacitySets = categorizedCapacitySets.value.get(
      portScopeKey(portComplex, directionScope)
    )
    if (portCapacitySets) {
      for (const capacitySet of portCapacitySets.tagSets.values()) {
        if (capacitySet.dedicated) {
          if (capacitySet.containerTag === null) {
            throw new Error('Global capacity set cannot be dedicated')
          }
          tags.add(capacitySet.containerTag)
        }
      }
    }
    return Array.from(tags)
  }
  const numCustomerSets = computed(() => {
    let total = 0
    for (const portCapacitySets of categorizedCapacitySets.value.values()) {
      total += portCapacitySets.customerSets.size
    }
    return total
  })
  const numTagSets = computed(() => {
    let total = 0
    for (const portCapacitySets of categorizedCapacitySets.value.values()) {
      total += portCapacitySets.tagSets.size
    }
    return total
  })

  function getCustomerSets(
    port: PortComplex,
    directionScope: DirectionScope
  ): CapacitySet[] {
    const portCapacitySets = categorizedCapacitySets.value.get(
      portScopeKey(port, directionScope)
    )
    return portCapacitySets
      ? Array.from(portCapacitySets.customerSets.values())
      : []
  }

  function getTagSets(
    port: PortComplex,
    directionScope: DirectionScope
  ): CapacitySet[] {
    const portCapacitySets = categorizedCapacitySets.value.get(
      portScopeKey(port, directionScope)
    )
    return portCapacitySets ? Array.from(portCapacitySets.tagSets.values()) : []
  }
  function getFleetSets(
    port: PortComplex,
    directionScope: DirectionScope
  ): CapacitySet[] {
    const portCapacitySets = categorizedCapacitySets.value.get(
      portScopeKey(port, directionScope)
    )
    return portCapacitySets
      ? Array.from(portCapacitySets.fleetSets.values())
      : []
  }
  function getCapacitySet(
    customerID: number | null,
    containerTag: string | null,
    portComplex: PortComplex,
    directionScope: DirectionScope
  ): CapacitySet | undefined {
    const portCapacitySets = categorizedCapacitySets.value.get(
      portScopeKey(portComplex, directionScope)
    )
    if (!portCapacitySets) return undefined
    if (customerID) {
      for (const set of portCapacitySets.customerSets.values()) {
        if (set.customerID === customerID) return set
      }
      return undefined
    }
    if (containerTag) {
      for (const set of portCapacitySets.tagSets.values()) {
        if (set.containerTag === containerTag) return set
      }
      return undefined
    }
    return portCapacitySets.fleetSets.values().next().value
  }
  return {
    // Refs
    categorizedCapacitySets,
    loading,
    activeCapacitySet,
    viewedDirectionScope,
    saving,
    // Functions
    generateBuckets,
    loadBucketsIfNotLoaded,
    saveCapacitySet,
    deleteCapacitySet,
    setIndependentCapacityForScope,
    activateCapacityManagement,
    setCapacitySetsFromAPI,
    setCapacitySet,
    getFleetSets,
    setActiveCapacitySet,
    getBucketsForTime,
    getCapacitySet,
    getCapacitySetById,
    selectDefaultCapacitySet,
    clearActiveCapacitySet,
    getCustomerSets,
    getTagSets,
    getDedicatedCustomerCapacitySetIdsForPort,
    getDedicatedTagCapacityIdsForPort,
    // Computed properties
    setup,
    numCustomerSets,
    numTagSets,
  }
})

export function generateEmpty(
  bucketMinutes: number,
  setIdentifiers: CapacitySetIdentifiers,
  dedicated: boolean,
  demoMode: boolean
): CapacityBucket[] {
  const buckets: CapacityBucket[] = []
  let currentMinuteOfWeekStart = 0
  while (currentMinuteOfWeekStart < 7 * 24 * 60) {
    buckets.push(
      new CapacityBucket(
        {
          startMinuteOfWeek: currentMinuteOfWeekStart,
          bucketMinutes,
          customer: setIdentifiers.customer,
          containerTag: setIdentifiers.containerTag,
          dedicated,
          portComplex: setIdentifiers.portComplex,
        },
        demoMode
      )
    )
    currentMinuteOfWeekStart = currentMinuteOfWeekStart + bucketMinutes
  }
  return buckets
}
